import React, { useEffect } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import TenantCommercialsApi, {
  ITenantCommercialsBackendData
} from 'api/b2b/TenantCommercial'
import { findByValue } from 'helpers/b2b'
import OrbitSession from 'hooks/useCookies'

import { IAutoCompleteDropdown } from '../../Registration/components/TDPDetails'
import { commercialTypes, creditTermOptions } from '../../Registration/contants'

interface IFormInput {
  commercialType: IAutoCompleteDropdown
  availableLimit: string
  outstandingBalance: string
  walletBalance: string
  currency: string
  creditPeriod: IAutoCompleteDropdown
  creditLimit: string
  creditAction: string
  creditAmount: string
}

export default function CommercialsEdit() {
  const [searchParams] = useSearchParams()
  const companyId = searchParams.get('id') || ''

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      commercialType: commercialTypes[0],
      availableLimit: '',
      outstandingBalance: '',
      walletBalance: '',
      currency: 'USA',
      creditPeriod: creditTermOptions[0],
      creditLimit: '',
      creditAction: 'addCredit',
      creditAmount: ''
    }
  })

  const faberToken = OrbitSession.get('faberToken')

  const { mutate: updateTenantCommercials, isLoading: isUpdatingData } =
    useMutation(
      (data: ITenantCommercialsBackendData) =>
        TenantCommercialsApi.updateTenantCommercials(data, faberToken),
      {
        onSuccess: () => {
          toast.success('Commercial is updated successfully', {
            autoClose: 2000,
            hideProgressBar: false
          })
          // onSaveSuccess('#rebate')
        },
        onError: (error) => {
          console.error('API Error:', error)
          toast.error('Failed to create commercials', {
            autoClose: 2000,
            hideProgressBar: false
          })
        }
      }
    )

  // Mutation for fetching the existing commercials data
  const { data: commercialsData, isLoading: commercialsLoading } = useQuery(
    ['specific-commercial-details', companyId], // query key
    async () => {
      const { data } = await TenantCommercialsApi.getCommercialsById(
        faberToken,
        companyId
      )
      return data
    },
    {
      enabled: !!companyId,
      staleTime: 1000 * 60 * 30, // 30 minutes
      cacheTime: 1000 * 60 * 60, // 1 hour
      onError: (error) => {
        console.error('Failed to fetch countries:', error)
      }
    }
  )

  useEffect(() => {
    if (commercialsData) {
      setValue('creditLimit', commercialsData.credit_limit)
      setValue(
        'commercialType',
        findByValue(commercialTypes, commercialsData.commercial_type) ||
          commercialTypes[0]
      )
      setValue(
        'creditPeriod',
        findByValue(creditTermOptions, commercialsData.credit_terms_period) ||
          creditTermOptions[0]
      )
      setValue(
        'walletBalance',
        commercialsData.wallet_balance
      ),
      setValue(
        'outstandingBalance',
        commercialsData.outstanding_balance
      ),
      setValue(
        'availableLimit',
        commercialsData.available_limit
      )
    }
  }, [commercialsData, setValue])

  // ToDO:From where did the remaining field value will come
  // ToDO:Is the add and set credit action is correct?

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    // An extra check to make sure the company id is not empty
    if (!companyId) {
      toast.error('Please select a company first from TDP Details list')
      return
    }

    const payload: ITenantCommercialsBackendData = {
      id: commercialsData?.id || null,
      tenant_id: companyId,
      commercial_type: data.commercialType.value.toUpperCase(),
      credit_terms_period: data.creditPeriod.value,
      credit_limit: data.creditLimit,
      is_active: true,
      created_by: OrbitSession.get('email') || '',
      updated_by: OrbitSession.get('email') || ''
    }
    // Add the existing credit limit if the action is to add credit
    payload.credit_limit =
      data.creditAction === 'addCredit'
        ? String(Number(payload.credit_limit) + Number(data.creditAmount))
        : String(Number(data.creditAmount))
    updateTenantCommercials(payload)
  }

  return (
    <Box>
      {commercialsLoading ? (
        <CircularProgress />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display='flex' alignItems='center'>
            <Typography sx={{ mr: '10px' }}>Commercial Type*</Typography>
            <Controller
              name='commercialType'
              control={control}
              rules={{ required: { value: true, message: 'Required' } }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  disablePortal
                  disableClearable
                  options={commercialTypes}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(opt, val) => opt.value === val.value}
                  value={field.value || null}
                  onChange={(_, data) => field.onChange(data)}
                  size='small'
                  sx={{ width: '20%' }}
                  renderInput={(params) => <TextField {...params} />}
                />
              )}
            />
          </Box>

          <Box display='flex' alignItems='center'>
            <Typography sx={{ mr: '10px' }}>Currency*</Typography>
            <Controller
              name='currency'
              control={control}
              rules={{
                required: { value: true, message: 'Currency is Required' }
              }}
              render={({ field }) => (
                <TextField
                  id='currency'
                  variant='outlined'
                  size='small'
                  disabled={true}
                  {...field}
                  error={!!errors.currency}
                  helperText={errors.currency?.message}
                />
              )}
            />
          </Box>

          <Box display='flex' alignItems='center'>
            <Typography sx={{ mr: '10px' }}>Credit Terms Period*</Typography>
            <Controller
              name='creditPeriod'
              control={control}
              rules={{ required: { value: true, message: 'Required' } }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  disablePortal
                  disableClearable
                  options={creditTermOptions}
                  onChange={(_, data) => field.onChange(data)}
                  size='small'
                  sx={{ width: '20%' }}
                  renderInput={(params) => <TextField {...params} />}
                />
              )}
            />
          </Box>

          <Box display='flex' alignItems='center'>
            <Typography sx={{ mr: '10px' }}>Credit Limit</Typography>
            <Controller
              name='creditLimit'
              control={control}
              render={({ field }) => (
                <TextField
                  id='creditLimit'
                  variant='outlined'
                  size='small'
                  disabled={true}
                  {...field}
                />
              )}
            />
          </Box>

          <Grid container xs={12}>
            <Grid item md={4} xs={12}>
              <FormControl component='fieldset' margin='normal'>
                <Controller
                  name='creditAction'
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field} row>
                      <FormControlLabel
                        value='addCredit'
                        control={<Radio />}
                        label='Add Credit'
                      />
                      <FormControlLabel
                        value='setCredit'
                        control={<Radio />}
                        label='Set Credit Limit'
                      />
                    </RadioGroup>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item md={4} xs={12}>
              <Box display='flex' alignItems='center'>
                <Typography sx={{ mr: '10px' }}>Amount</Typography>
                <Controller
                  name='creditAmount'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type='number'
                      id='creditAmount'
                      variant='outlined'
                      size='small'
                      {...field}
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>

          <Box display='flex' alignItems='center'>
            <Typography sx={{ mr: '10px' }}>Wallet Balance</Typography>
            <TextField
              id='outlined-basic'
              variant='outlined'
              size='small'
              value={getValues('walletBalance')}
              disabled={true}
            />
          </Box>

          <Box display='flex' alignItems='center'>
            <Typography sx={{ mr: '10px' }}>Outstanding Balance</Typography>
            <TextField
              id='outlined-basic'
              variant='outlined'
              size='small'
              value={getValues('outstandingBalance')}
              disabled={true}
            />
          </Box>

          <Box display='flex' alignItems='center'>
            <Typography sx={{ mr: '10px' }}>Available Limit</Typography>
            <TextField
              id='outlined-basic'
              variant='outlined'
              size='small'
              value={getValues('availableLimit')}
              disabled={true}
            />
          </Box>

          <Box sx={{ mt: '50px' }}>
            <Button
              type='submit'
              variant='contained'
              disableElevation
              disabled={isUpdatingData}
            >
              {isUpdatingData ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Save Changes'
              )}
            </Button>
          </Box>
        </form>
      )}
    </Box>
  )
}
